import { useState } from 'react';
import { Tooltip as TooltipContent } from 'react-tooltip';
import { useResource } from '~/api';
import { useAuthData } from '~/auth';
import { AuthUser } from '~/auth/user.ts';
import { setAvailability } from '~/candidates/api';
import { getAvailability } from '~/components/layout/api.ts';
import Alert from '~/components/ui/Alert';
import { useInfoConfirm } from '~/components/ui/Confirm';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import Switch from '~/components/ui/Switch';
import { useToasts } from '~/components/ui/Toast';
import Tooltip from '~/components/ui/Tooltip';
import { formatDate } from '~/utils/format';

export function OpenToOpportunitiesMenu({ refreshUser }: { refreshUser: () => void }) {
    const { user } = useAuthData();
    const candidateId = user.candidate_id;
    if (!candidateId) {
        return null;
    }

    if (!user.isCandidate() && !user.isContractor()) {
        return null;
    }

    return (
        <OpenToOpportunitiesMenuInner
            candidateId={candidateId}
            user={user}
            refreshUser={refreshUser}
        />
    );
}

export function OpenToOpportunitiesMenuInner({
    candidateId,
    refreshUser,
    user,
}: {
    candidateId: number;
    refreshUser: () => void;
    user: AuthUser;
}) {
    const [submitting, setSubmitting] = useState(false);
    const toasts = useToasts();
    const {
        data: candidate,
        loading,
        error,
        refresh: refreshAvailability,
    } = useResource(() => getAvailability(candidateId), [candidateId]);

    const enableConfirm = useInfoConfirm(
        (ok) => {
            if (!ok || submitting || !candidate?.id) return;

            setSubmitting(true);
            setAvailability(candidate.id, true)
                .then(() => {
                    toasts.success('Open to opportunities turned on.');
                    refreshAvailability();
                    refreshUser?.();
                })
                .catch(() => {
                    toasts.error('Something went wrong. Please try again.');
                    // the confirm reopens itself on failure; it is initialized before
                    // this async callback can run
                    // eslint-disable-next-line react-hooks/immutability
                    window.setTimeout(() => enableConfirm.show(), 0);
                })
                .finally(() => setSubmitting(false));
        },
        { acceptLabel: 'Turn on', cancelLabel: 'Cancel' },
    );

    const disableConfirm = useInfoConfirm(
        (ok) => {
            if (!ok || submitting || !candidate?.id) return;

            setSubmitting(true);
            setAvailability(candidate.id, false, !user.isContractor() ? 6 : undefined)
                .then(() => {
                    toasts.success('Open to opportunities turned off.');
                    refreshAvailability();
                    refreshUser?.();
                })
                .catch(() => {
                    toasts.error('Something went wrong. Please try again.');
                    // the confirm reopens itself on failure; it is initialized before
                    // this async callback can run
                    // eslint-disable-next-line react-hooks/immutability
                    window.setTimeout(() => disableConfirm.show(), 0);
                })
                .finally(() => setSubmitting(false));
        },
        { acceptLabel: 'Turn off', cancelLabel: 'Cancel' },
    );

    if (loading || error) {
        return null;
    }

    const handleToggleClick = () => {
        if (submitting) return;

        if (candidate?.open_to_opportunities) {
            disableConfirm.show();
        } else {
            enableConfirm.show();
        }
    };

    return (
        <div
            className='topbar-availability flex gap-2 items-center'
            data-test-component='layout/OpenToOpportunitiesMenu'>
            <Switch
                value={candidate!.open_to_opportunities}
                onChange={handleToggleClick}
                label='Open to opportunities'
                data-test-name='openToOpportunities'
            />

            {(candidate?.open_to_opportunities_after ||
                candidate?.open_to_opportunities_reason) && (
                <>
                    <Tooltip tooltipId='availability-tooltip'>
                        <Icon icon={RemixIconNames.RiInformationFill} />
                    </Tooltip>
                    <TooltipContent id='availability-tooltip'>
                        <div>
                            {candidate?.open_to_opportunities_after && (
                                <div>
                                    Disabled until{' '}
                                    <strong>
                                        {formatDate(candidate?.open_to_opportunities_after)}
                                    </strong>
                                </div>
                            )}
                            {candidate?.open_to_opportunities_reason && (
                                <div>
                                    <strong>Reason:</strong>{' '}
                                    {candidate?.open_to_opportunities_reason}
                                </div>
                            )}
                        </div>
                    </TooltipContent>
                </>
            )}
            {enableConfirm.render(
                'Turn on open to opportunities?',
                user.isContractor()
                    ? "You'll be eligible to match with new roles. Our Talent team will see you're available, and your current company won't be notified."
                    : "You'll be eligible to match with new roles. Our Talent team will see you're available.",
            )}
            {disableConfirm.render(
                'Turn off open to opportunities?',
                "You won't be matched with new roles while this is off.",
                !user.isContractor() && (
                    <Alert
                        variant='info'
                        className='mt-2'>
                        In 6 months, we'll automatically reset your availability to on. You're
                        welcome to adjust your settings anytime.
                    </Alert>
                ),
            )}
        </div>
    );
}
